import { useState } from "react";
import { AccordionEditor } from "./AccordionEditor";
import { AddressGridEditor } from "./AddressGridEditor";
import { BannerEditor } from "./BannerEditor";
import {
  toAccordionContent,
  toBannerContent,
  toButtonsContent,
  toCallToActionContent,
  toCardGridContent,
  toGalleryContent,
  toHeroContent,
  toIframeContent,
  toIconListContent,
  toPeopleContent,
  toStatsContent,
  toStepsContent,
  toTextContent,
  toVideoContent,
} from "./blockContent";
import { ButtonsEditor } from "./ButtonsEditor";
import { CallToActionEditor } from "./CallToActionEditor";
import { CardGridEditor } from "./CardGridEditor";
import { CardModalGridEditor } from "./CardModalGridEditor";
import { GalleryEditor } from "./GalleryEditor";
import { HeroEditor } from "./HeroEditor";
import { IconListEditor } from "./IconListEditor";
import { IframeEditor } from "./IframeEditor";
import { PeopleEditor } from "./PeopleEditor";
import { StatsEditor } from "./StatsEditor";
import { StepsEditor } from "./StepsEditor";
import { TextBlockEditor } from "./TextBlockEditor";
import type { AddressGridContent, BlockContentErrors, CardModalGridContent } from "./types";
import { VideoEditor } from "./VideoEditor";
import { LANDING_PAGE_TYPE } from "../pageTypeRegistry";
import styles from "../CMS.module.css";

type BlockContentEditorProps = {
  blockType: string;
  pageType: string;
  value: Record<string, unknown> | null;
  onChange: (value: Record<string, unknown> | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
  errors?: BlockContentErrors;
};

function isCardModalGridContent(
  value: Record<string, unknown> | null,
): value is CardModalGridContent {
  if (!value) return false;
  return Array.isArray(value.cards);
}

function isAddressGridContent(value: Record<string, unknown> | null): value is AddressGridContent {
  if (!value) {
    return false;
  }
  return Array.isArray(value.addresses);
}

function toCardModalGridContent(value: Record<string, unknown> | null): CardModalGridContent {
  if (isCardModalGridContent(value)) return value;
  return { cards: [] };
}

function toAddressGridContent(value: Record<string, unknown> | null): AddressGridContent {
  if (isAddressGridContent(value)) {
    return value;
  }
  return { addresses: [] };
}

export function BlockContentEditor({
  blockType,
  pageType,
  value,
  onChange,
  onUploadingChange,
  errors = {},
}: BlockContentEditorProps) {
  // Fallback JSON textarea for unknown block types
  const [jsonText, setJsonText] = useState(() => (value ? JSON.stringify(value, null, 2) : ""));
  const [jsonError, setJsonError] = useState<string | null>(null);

  function handleJsonChange(text: string) {
    setJsonText(text);
    if (!text.trim()) {
      setJsonError(null);
      onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      setJsonError(null);
      onChange(parsed);
    } catch {
      setJsonError("Invalid JSON");
    }
  }

  switch (blockType) {
    case "block_card_grid":
      return (
        <CardGridEditor
          value={toCardGridContent(value)}
          errors={errors}
          onUploadingChange={onUploadingChange}
          onChange={updated => onChange(updated as unknown as Record<string, unknown>)}
        />
      );

    case "block_card_modal_grid":
      return (
        <CardModalGridEditor
          onUploadingChange={onUploadingChange}
          value={toCardModalGridContent(value)}
          onChange={updated => onChange(updated as unknown as Record<string, unknown>)}
        />
      );

    case "block_iframe":
      return (
        <IframeEditor
          value={toIframeContent(value)}
          errors={errors}
          onChange={updated => onChange(updated as unknown as Record<string, unknown>)}
        />
      );

    case "block_address_grid":
      return (
        <AddressGridEditor
          value={toAddressGridContent(value)}
          onChange={updated => onChange(updated as unknown as Record<string, unknown>)}
        />
      );

    case "block_hero":
      return (
        <HeroEditor
          onUploadingChange={onUploadingChange}
          value={toHeroContent(value)}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_text":
      return (
        <TextBlockEditor
          value={toTextContent(value)}
          errors={errors}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_stats":
      return <StatsEditor value={toStatsContent(value)} onChange={updated => onChange(updated)} />;

    case "block_people":
      return (
        <PeopleEditor
          onUploadingChange={onUploadingChange}
          value={toPeopleContent(value)}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_icon_list":
      return (
        <IconListEditor
          onUploadingChange={onUploadingChange}
          value={toIconListContent(value)}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_buttons":
      return (
        <ButtonsEditor value={toButtonsContent(value)} onChange={updated => onChange(updated)} />
      );

    case "block_gallery":
      return (
        <GalleryEditor
          onUploadingChange={onUploadingChange}
          value={toGalleryContent(value)}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_banner":
      return (
        <BannerEditor
          value={toBannerContent(value)}
          errors={errors}
          onUploadingChange={onUploadingChange}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_steps":
      return (
        <StepsEditor
          value={toStepsContent(value)}
          errors={errors}
          onUploadingChange={onUploadingChange}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_video":
      return (
        <VideoEditor
          value={toVideoContent(value)}
          errors={errors}
          onUploadingChange={onUploadingChange}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_accordion":
      return (
        <AccordionEditor
          value={toAccordionContent(value)}
          errors={errors}
          showBackground={pageType === LANDING_PAGE_TYPE}
          onChange={updated => onChange(updated)}
        />
      );

    case "block_call_to_action":
      return (
        <CallToActionEditor
          value={toCallToActionContent(value)}
          errors={errors}
          onChange={updated => onChange(updated)}
        />
      );

    default:
      return (
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="block-content-json">
            Content (JSON)
          </label>
          <textarea
            id="block-content-json"
            className={styles.textarea}
            value={jsonText}
            onChange={e => handleJsonChange(e.target.value)}
            placeholder='{"heading": "Hello", "body": "..."}'
            rows={6}
          />
          {jsonError && <span className={styles.jsonError}>{jsonError}</span>}
        </div>
      );
  }
}
